import {
  DAWAAddress,
  DAWAAutocompleteSuggestion,
  findAddressById,
  resolveAddressSuggestion,
  toAddressFromDAWAAddress,
} from '@bodilenergy/address';

export type AddressSelection =
  | { status: 'refine'; search: string }
  | { status: 'resolved'; address: DAWAAddress }
  | { status: 'not_found' }
  | { status: 'failed'; error: unknown }
  | { status: 'stale' };

/**
 * Turns a selected suggestion into a complete address. Street and house hits are refinements of the
 * search; address hits are hydrated before use. A newer selection or cancel() makes the pending one
 * 'stale', which callers ignore. 'failed' is a provider error, 'not_found' a confirmed missing address.
 */
export const createAddressSelector = () => {
  let pending: AbortController | undefined;

  const cancel = () => {
    pending?.abort();
    pending = undefined;
  };

  const select = async (suggestion: DAWAAutocompleteSuggestion): Promise<AddressSelection> => {
    cancel();

    if (suggestion.type === 'vejnavn') {
      return { status: 'refine', search: suggestion.forslagstekst };
    }

    const controller = new AbortController();
    pending = controller;

    try {
      const address = await resolveAddressSuggestion(suggestion, { signal: controller.signal });

      if (controller.signal.aborted) {
        return { status: 'stale' };
      }

      return address ? { status: 'resolved', address } : { status: 'not_found' };
    } catch (error) {
      return controller.signal.aborted ? { status: 'stale' } : { status: 'failed', error };
    } finally {
      if (pending === controller) {
        pending = undefined;
      }
    }
  };

  return { select, cancel };
};

/** Deep link address by DAR address ID, keeping floor and door. Unknown IDs give undefined; outages throw. */
export const findAddressByDeepLinkId = async (addressId: string, signal?: AbortSignal) => {
  const data = await findAddressById(addressId, { signal });

  return data ? toAddressFromDAWAAddress(data) : undefined;
};
